import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { RotateCw, ArrowLeft, ArrowRight } from "lucide-react";
import { api } from "@/api/client";
import { useAuth } from "@/contexts/AuthContext";
import { useI18n } from "@/contexts/I18nContext";
import { fmtNum, fmtDateTime } from "@/lib/format";

export default function SystemHealthPage() {
  const { user } = useAuth();
  const { theme, dir, lang } = useI18n();  const navigate = useNavigate();
  const [health, setHealth] = useState(null);
  const [version, setVersion] = useState(null);
  const [msg, setMsg] = useState("");
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState("");
  const [err, setErr] = useState("");

  const isRoot = user?.role?.is_root;

  useEffect(() => {
    if (!isRoot) {
      navigate("/", { replace: true });
    }
  }, [isRoot, navigate]);

  const load = async () => {
    setErr("");
    try {
      const [h, v] = await Promise.all([
        api.get("/admin/system-health").then((r) => r.data),
        api.get("/admin/system/version").then((r) => r.data),
      ]);
      setHealth(h);
      setVersion(v);
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر التحميل");
    }
  };

  useEffect(() => { if (isRoot) load(); }, [isRoot]);

  const triggerForceUpdate = async () => {
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/system/force-update", { message: msg.trim() || null });
      setVersion({ ...version, ...data });
      setConfirmOpen(false);
      setMsg("");
      setToast(lang === "ar"
        ? `تم نشر تحديث إجباري للجميع — الإصدار v${data.version}`
        : `Force update broadcast — v${data.version}`);
      setTimeout(() => setToast(""), 5000);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "تعذّر التحديث");
    } finally { setBusy(false); }
  };

  /** STAS-only — force this browser to refresh from scratch right now.
   *  Runs the same cache/SW/storage cleanup as the auto banner. Useful
   *  when STAS wants to verify a deploy locally before pushing the
   *  broadcast to everyone. */
  const refreshMyBrowser = async () => {
    if (!window.confirm(lang === "ar"
      ? "سيتم مسح الكاش وSW وتسجيل دخول قد ينقطع لثوانٍ، ثم إعادة تحميل الصفحة. متابعة؟"
      : "Cache, SW, and storage will be cleared and the page reloaded. Continue?")) return;
    if (typeof window.__vtaForceRefresh === "function") {
      await window.__vtaForceRefresh();
    } else {
      // Fallback — minimal cleanup
      try {
        if (typeof caches !== "undefined") {
          const keys = await caches.keys();
          await Promise.all(keys.map((k) => caches.delete(k)));
        }
        if ("serviceWorker" in navigator) {
          const regs = await navigator.serviceWorker.getRegistrations();
          await Promise.all(regs.map((r) => r.unregister()));
        }
      } catch { /* ignore */ }
      window.location.replace(window.location.pathname + "?_t=" + Date.now());
    }
  };

  if (!isRoot) return null;

  const T = lang === "ar"
    ? {
        title: "صحة النظام",
        sub: "لوحة تحكم STAS — حالة الخادم وقاعدة البيانات والمؤشرات",
        update_title: "نشر تحديث إجباري",
        update_desc: "اضغط الزر التالي لإرغام جميع المستخدمين الحاليين على تحميل آخر نسخة من النظام تلقائياً (مسح الكاش + إعادة تحميل). يحل مشاكل النسخ القديمة والتعليق من جذورها.",
        current_version: "الإصدار الحالي",
        last_published: "آخر نشر",
        published_by: "بواسطة",
        msg_label: "رسالة اختيارية للمستخدمين",
        msg_ph: "مثلاً: تحسينات على البوابة",
        force_btn: "نشر التحديث للجميع الآن",
        confirm_title: "تأكيد نشر التحديث",
        confirm_body: "سيظهر للجميع شريط في الأعلى يطلب منهم التحديث. عند الضغط سيُمسح الكاش ويُعاد التحميل بآخر نسخة. هل أنت متأكد؟",
        cancel: "إلغاء",
        confirm_yes: "نعم، انشر الآن",
        server: "الخادم",
        database: "قاعدة البيانات",
        storage: "التخزين",
        metrics: "المؤشرات",
        users_total: "إجمالي المستخدمين",
        internal: "موظفون",
        clients: "عملاء",
        apps_total: "إجمالي المعاملات",
        payments_total: "إجمالي المدفوعات",
        pending: "قيد المراجعة",
        approved: "مُعتمد",
        rejected: "مرفوض",
        attachments_total: "المرفقات",
        audit_total: "سجل التدقيق",
        uptime: "زمن التشغيل",
        ping: "زمن الاستجابة",
        used: "مستخدم",
        free: "متاح",
        back: "رجوع",
      }
    : {
        title: "System Health",
        sub: "STAS dashboard — server, database and metrics",
        update_title: "Broadcast a Force Update",
        update_desc: "Click below to force every active client to clear cache and reload the latest version automatically. Solves stale-version and stuck-state issues at the root.",
        current_version: "Current version",
        last_published: "Last published",
        published_by: "By",
        msg_label: "Optional message to users",
        msg_ph: "e.g. Improvements to the portal",
        force_btn: "Broadcast update now",
        confirm_title: "Confirm broadcast",
        confirm_body: "A banner will appear for every active user asking them to update. On click, caches will be cleared and the page reloaded with the latest version. Continue?",
        cancel: "Cancel",
        confirm_yes: "Yes, broadcast now",
        server: "Server",
        database: "Database",
        storage: "Storage",
        metrics: "Metrics",
        users_total: "Total users",
        internal: "Internal",
        clients: "Clients",
        apps_total: "Applications",
        payments_total: "Payments",
        pending: "Pending",
        approved: "Approved",
        rejected: "Rejected",
        attachments_total: "Attachments",
        audit_total: "Audit logs",
        uptime: "Uptime",
        ping: "Ping",
        used: "used",
        free: "free",
        back: "Back",
      };

  const css = makeStyles(theme);
  const m = health?.metrics || {};
  const stor = health?.storage;

  return (
    <div dir={dir} style={css.page} data-testid="system-health-page">
      <div style={css.headerRow}>
        <div>
          <h1 style={css.h1}>{T.title}</h1>
          <div style={css.sub}>{T.sub}</div>
        </div>
        <button onClick={() => navigate("/")} style={css.backBtn} data-testid="back-dashboard">
          {dir === "rtl" ? <ArrowRight size={14} strokeWidth={2.2} /> : <ArrowLeft size={14} strokeWidth={2.2} />}
          {T.back}
        </button>
      </div>

      {toast && (
        <div style={css.toast} data-testid="toast">{toast}</div>
      )}
      {err && <div style={css.err}>{err}</div>}

      {/* Force Update Card */}
      <section style={css.heroCard} data-testid="force-update-card">
        <div style={css.heroHead}>
          <div style={css.heroTitle}>{T.update_title}</div>
          <span style={css.versionBadge} data-testid="current-version">
            v{fmtNum(version?.version ?? 1)}
          </span>
        </div>
        <p style={css.heroDesc}>{T.update_desc}</p>

        <div style={css.heroMeta}>
          <Meta theme={theme} label={T.last_published} value={version?.published_at ? fmtDateTime(version.published_at) : "—"} />
          <Meta theme={theme} label={T.published_by} value={version?.published_by?.full_name || "—"} />
        </div>

        <label style={css.label}>{T.msg_label}</label>
        <input
          type="text"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          placeholder={T.msg_ph}
          maxLength={200}
          style={css.input}
          data-testid="force-update-message"
        />

        <button
          onClick={() => setConfirmOpen(true)}
          style={css.forceBtn}
          data-testid="open-force-update"
          disabled={busy}
        >
          <RotateCw size={16} strokeWidth={2.3} />
          {T.force_btn}
        </button>

        <button
          onClick={refreshMyBrowser}
          style={{ ...css.forceBtn, background: "transparent", color: theme.text, border: `1px solid ${theme.borderAccent}`, marginTop: 8 }}
          data-testid="refresh-my-browser"
        >
          <RotateCw size={14} strokeWidth={2.3} />
          {lang === "ar" ? "فرض تحديث متصفحي الآن (مسح كامل)" : "Force refresh THIS browser now (full clean)"}
        </button>
      </section>

      {/* Production Activation */}
      <ProductionActivationCard theme={theme} lang={lang} dir={dir} />

      {/* STAS — Trial-phase system reset + downloadable JSON archive */}
      <SystemResetCard theme={theme} lang={lang} dir={dir} />

      {/* STAS — Finance-only reset (zero the accountant dashboard) */}
      <FinanceResetCard theme={theme} lang={lang} dir={dir} />

      {/* Server / DB / Storage */}
      <section style={css.grid3}>
        <Card theme={theme} title={T.server}>
          <Row k={T.uptime} v={`${fmtNum(Math.floor((health?.server?.uptime_seconds || 0) / 60))} min`} />
          <Row k="Python" v={health?.server?.python_version || "—"} />
        </Card>
        <Card theme={theme} title={T.database}>
          <Row k="MongoDB" v={health?.database?.ok ? "● online" : "● offline"} good={health?.database?.ok} />
          <Row k={T.ping} v={health?.database?.ping_ms != null ? `${fmtNum(health.database.ping_ms)} ms` : "—"} />
        </Card>
        <Card theme={theme} title={T.storage}>
          <Row k={T.used} v={stor ? `${fmtNum(stor.used_percent)}%` : "—"} />
          <Row k={T.free} v={stor ? `${fmtNum(Math.floor(stor.free_bytes / (1024 ** 3)))} GB` : "—"} />
        </Card>
      </section>

      {/* Metrics */}
      <section style={css.gridMetrics}>
        <Stat theme={theme} label={T.users_total} value={fmtNum(m?.users?.total)} testId="metric-users" />
        <Stat theme={theme} label={T.internal} value={fmtNum(m?.users?.internal)} />
        <Stat theme={theme} label={T.clients} value={fmtNum(m?.users?.clients)} />
        <Stat theme={theme} label={T.apps_total} value={fmtNum(m?.applications_total)} testId="metric-apps" />
        <Stat theme={theme} label={T.payments_total} value={fmtNum(m?.payments?.total)} />
        <Stat theme={theme} label={T.pending} value={fmtNum(m?.payments?.pending)} />
        <Stat theme={theme} label={T.approved} value={fmtNum(m?.payments?.approved)} color="#16a34a" />
        <Stat theme={theme} label={T.rejected} value={fmtNum(m?.payments?.rejected)} color="#dc2626" />
        <Stat theme={theme} label={T.attachments_total} value={fmtNum(m?.attachments_total)} />
        <Stat theme={theme} label={T.audit_total} value={fmtNum(m?.audit_logs_total)} />
      </section>

      {/* Confirm modal */}
      {confirmOpen && (
        <div style={css.modalBg} onClick={() => !busy && setConfirmOpen(false)} data-testid="confirm-modal">
          <div style={css.modal} onClick={(e) => e.stopPropagation()}>
            <div style={css.modalTitle}>{T.confirm_title}</div>
            <p style={css.modalBody}>{T.confirm_body}</p>
            {msg && (
              <div style={css.msgPreview}>"{msg}"</div>
            )}
            <div style={css.modalActions}>
              <button onClick={() => setConfirmOpen(false)} disabled={busy} style={css.btnGhost} data-testid="cancel-update">
                {T.cancel}
              </button>
              <button onClick={triggerForceUpdate} disabled={busy} style={css.btnDanger} data-testid="confirm-force-update">
                {busy ? "…" : T.confirm_yes}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function Card({ title, children, theme }) {
  return (
    <div style={{
      background: theme.surfaceCard, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 14, padding: 16,
    }}>
      <div style={{ fontSize: 12, color: theme.textMuted, marginBottom: 10, fontWeight: 800, letterSpacing: 0.4 }}>{title}</div>
      {children}
    </div>
  );
}

function Row({ k, v, good }) {
  return (
    <div style={{ display: "flex", justifyContent: "space-between", padding: "6px 0", fontSize: 13 }}>
      <span style={{ opacity: 0.7 }}>{k}</span>
      <span style={{ fontWeight: 800, color: good ? "#16a34a" : "inherit" }}>{v}</span>
    </div>
  );
}

function ProductionActivationCard({ theme, lang, dir }) {
  const [state, setState] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(0); // 0=idle, 1=first confirm, 2=second confirm

  const load = async () => {
    try { const { data } = await api.get("/system-state"); setState(data); }
    catch (e) { setErr(e?.response?.data?.detail || "تعذّر التحميل"); }
  };
  useEffect(() => { load(); }, []);

  const activate = async () => {
    setBusy(true); setErr("");
    try {
      await api.post("/admin/system-state/activate");
      await load(); setStep(0);
    } catch (e) { setErr(e?.response?.data?.detail || "فشل التفعيل"); }
    finally { setBusy(false); }
  };

  if (!state) return null;
  const isProd = !!state.is_production;
  const T = lang === "ar"
    ? {
        title: "حالة النظام للإنتاج",
        sub_test: "النظام حالياً في وضع تجريبي. كل المعاملات الحالية ستُؤرشف من الإحصائيات والتقييم عند التفعيل.",
        sub_prod: "النظام مفعّل للإنتاج. التقييم والـ Smart Routing وAI Insights نشطة.",
        mode: "الوضع",
        test_mode: "تجريبي",
        prod_mode: "إنتاج",
        activated_at: "تاريخ التفعيل",
        activate_btn: "🚀 تفعيل النظام للإنتاج",
        confirm1: "بالضغط على \"متابعة\"، كل المعاملات الحالية ستُؤرشف من الإحصائيات والتقييم. هل تريد المتابعة؟",
        confirm2: "تأكيد نهائي — هذا الإجراء لا يُستعاد. النظام سيبدأ بحساب التقييم وتوزيع المعاملات الذكي من هذه اللحظة. متأكد؟",
        cancel: "إلغاء",
        next: "متابعة",
        final: "نعم، فعّل الآن",
        active_pill: "● مفعّل",
        test_pill: "● تجريبي",
      }
    : {
        title: "Production State",
        sub_test: "System is in TEST mode. All current applications will be excluded from stats and evaluation on activation.",
        sub_prod: "System is in PRODUCTION. Evaluation, Smart Routing and AI Insights are active.",
        mode: "Mode",
        test_mode: "Test",
        prod_mode: "Production",
        activated_at: "Activated at",
        activate_btn: "🚀 Activate Production",
        confirm1: "Activating will exclude all current applications from stats and evaluation. Continue?",
        confirm2: "Final confirmation — this cannot be undone. Evaluation and smart routing will start from this moment. Sure?",
        cancel: "Cancel",
        next: "Continue",
        final: "Yes, activate now",
        active_pill: "● Active",
        test_pill: "● Test",
      };

  return (
    <section dir={dir} data-testid="prod-activation-card" style={{
      background: isProd ? "rgba(22,163,74,0.06)" : "rgba(245,158,11,0.06)",
      border: `2px solid ${isProd ? "rgba(22,163,74,0.45)" : "rgba(245,158,11,0.50)"}`,
      borderRadius: 14, padding: 18, marginBottom: 16,
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: 0, fontWeight: 900, color: theme.gold, fontSize: 15 }}>{T.title}</h3>
          <p style={{ margin: "8px 0 12px", fontSize: 12, color: theme.textSoft, lineHeight: 1.7 }}>
            {isProd ? T.sub_prod : T.sub_test}
          </p>
          <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 12 }}>
            <div><span style={{ opacity: 0.7 }}>{T.mode}: </span>
              <strong style={{ color: isProd ? "#16a34a" : "#d97706", fontWeight: 900 }}>
                {isProd ? T.active_pill : T.test_pill}
              </strong>
            </div>
            {isProd && state.activated_at && (
              <div><span style={{ opacity: 0.7 }}>{T.activated_at}: </span>
                <strong>{fmtDateTime(state.activated_at)}</strong>
              </div>
            )}
          </div>
        </div>
        {!isProd && (
          <button
            onClick={() => setStep(1)} disabled={busy}
            data-testid="prod-activate-btn"
            style={{
              padding: "12px 22px", fontWeight: 900, fontSize: 13,
              background: "linear-gradient(135deg,#16a34a,#10b981)",
              color: "#fff", border: "none", borderRadius: 10, cursor: "pointer",
              fontFamily: "inherit", boxShadow: "0 4px 12px rgba(22,163,74,0.25)",
            }}
          >{T.activate_btn}</button>
        )}
      </div>
      {err && <div style={{ marginTop: 10, fontSize: 12, color: "#dc2626" }}>{err}</div>}

      {step > 0 && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.75)",
          display: "flex", alignItems: "center", justifyContent: "center", zIndex: 9999, padding: 16,
        }} onClick={() => !busy && setStep(0)}>
          <div onClick={(e) => e.stopPropagation()} style={{
            background: theme.surface, border: `2px solid ${theme.borderAccent}`,
            borderRadius: 14, padding: 22, maxWidth: 500, width: "100%",
            color: theme.text,
          }}>
            <h3 style={{ margin: "0 0 12px", color: step === 1 ? theme.gold : "#dc2626", fontSize: 15, fontWeight: 900 }}>
              {step === 1 ? "تأكيد أولي" : "تأكيد نهائي"}
            </h3>
            <p style={{ fontSize: 13, lineHeight: 1.8, margin: "0 0 20px" }}>
              {step === 1 ? T.confirm1 : T.confirm2}
            </p>
            <div style={{ display: "flex", gap: 10, justifyContent: "flex-end" }}>
              <button onClick={() => setStep(0)} disabled={busy} style={{
                padding: "9px 16px", background: "transparent", color: theme.text,
                border: `1px solid ${theme.border}`, borderRadius: 8, cursor: "pointer",
                fontSize: 13, fontWeight: 700, fontFamily: "inherit",
              }}>{T.cancel}</button>
              {step === 1 ? (
                <button onClick={() => setStep(2)} data-testid="prod-confirm-step1" style={{
                  padding: "9px 18px", background: theme.goldGradient,
                  color: theme.name === "dark" ? "#0a0a0a" : "#fff",
                  border: "none", borderRadius: 8, fontSize: 13, fontWeight: 900,
                  cursor: "pointer", fontFamily: "inherit",
                }}>{T.next}</button>
              ) : (
                <button onClick={activate} disabled={busy} data-testid="prod-confirm-final" style={{
                  padding: "9px 18px", background: "linear-gradient(135deg,#dc2626,#b91c1c)",
                  color: "#fff", border: "none", borderRadius: 8, fontSize: 13, fontWeight: 900,
                  cursor: "pointer", fontFamily: "inherit",
                }}>{busy ? "…" : T.final}</button>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function Meta({ label, value, theme }) {
  return (
    <div style={{
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 10, padding: "10px 12px",
    }}>
      <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 700 }}>{value}</div>
    </div>
  );
}

/* ────────────────── STAS — System Reset & Archive ──────────────────
 * Lets STAS wipe operational data while keeping users/settings/branding.
 * Every reset creates a downloadable JSON snapshot first; archives can
 * be restored at any time. Confirmation requires typing RESET / SYSTEM. */
function SystemResetCard({ theme, lang, dir }) {
  const [archives, setArchives] = useState([]);
  const [label, setLabel] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [step, setStep] = useState(0); // 0=idle, 1=confirm phrase
  const [confirmPhrase, setConfirmPhrase] = useState("");
  const [toast, setToast] = useState("");

  const T = lang === "ar" ? {
    title: "تصفير النظام مع أرشفة (مرحلة التجارب)",
    desc: "هذا الزر يحفظ نسخة كاملة من المعاملات والمدفوعات والإشعارات والحسابات (JSON) ثم يصفّر النظام بالكامل. المستخدمون والإعدادات والشعار وكتالوج الأسعار لا تُمس.",
    label_input: "وصف الأرشيف (اختياري)",
    label_ph: "مثال: نسخة ما قبل الإطلاق الرسمي",
    btn_archive_reset: "أرشفة وتصفير النظام",
    confirm_title: "تأكيد التصفير",
    confirm_body: 'لتأكيد هذه العملية اكتب كلمة "تصفير" بالحرف بالضبط ثم اضغط تنفيذ. سيتم أولاً حفظ أرشيف كامل، ثم حذف المعاملات.',
    confirm_word: "تصفير",
    confirm_input_ph: "اكتب كلمة التأكيد",
    cancel: "إلغاء",
    do_it: "تنفيذ الآن",
    archives_title: "الأرشيفات السابقة",
    no_archives: "لا توجد أرشيفات بعد.",
    col_label: "الوصف",
    col_when: "التاريخ",
    col_who: "أنشأها",
    col_total: "العدد",
    download: "تحميل JSON",
    restore: "استرجاع",
    del: "حذف",
    restore_confirm: "تأكيد استرجاع هذا الأرشيف؟ سيتم استبدال كل المعاملات الحالية بالكامل.",
    del_confirm: "حذف هذا الأرشيف نهائياً؟",
    reset_done: (n) => `تم التصفير بنجاح — تم أرشفة ${n} وثيقة.`,
    restore_done: "تم استرجاع الأرشيف.",
    err_mismatch: 'كلمة التأكيد غير صحيحة — اكتب "تصفير" تماماً.',
  } : {
    title: "Archive & Reset System (Trial Phase)",
    desc: "Saves a full JSON snapshot of applications, payments, notifications and ledger entries, then wipes the operational data. Users, settings, branding and pricing catalog are NOT touched.",
    label_input: "Archive label (optional)",
    label_ph: "e.g. Pre-launch snapshot",
    btn_archive_reset: "Archive & reset",
    confirm_title: "Confirm reset",
    confirm_body: 'Type the word "RESET" exactly to confirm. The system will first store a full archive, then wipe operational data.',
    confirm_word: "RESET",
    confirm_input_ph: "Type the confirmation word",
    cancel: "Cancel",
    do_it: "Run now",
    archives_title: "Previous archives",
    no_archives: "No archives yet.",
    col_label: "Label",
    col_when: "When",
    col_who: "Created by",
    col_total: "Docs",
    download: "Download JSON",
    restore: "Restore",
    del: "Delete",
    restore_confirm: "Restore this archive in full? All current operational data will be replaced.",
    del_confirm: "Delete this archive permanently?",
    reset_done: (n) => `Reset complete — ${n} document(s) archived.`,
    restore_done: "Archive restored.",
    err_mismatch: 'Confirmation phrase mismatch — type "RESET" exactly.',
  };

  const load = async () => {
    setErr("");
    try {
      const { data } = await api.get("/admin/system/archives");
      setArchives(data.items || []);
    } catch (e) { setErr(e?.response?.data?.detail || "error"); }
  };
  useEffect(() => { load(); }, []);

  const startReset = () => {
    setStep(1);
    setConfirmPhrase("");
    setErr("");
  };

  const cancel = () => {
    setStep(0);
    setConfirmPhrase("");
  };

  const doReset = async () => {
    if (confirmPhrase.trim().toUpperCase() !== T.confirm_word.toUpperCase()) {
      setErr(T.err_mismatch);
      return;
    }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/system/archive-and-reset", {
        label: label.trim() || null,
      });
      setLabel(""); setStep(0); setConfirmPhrase("");
      setToast(T.reset_done(data.total_documents));
      setTimeout(() => setToast(""), 6000);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "error");
    } finally { setBusy(false); }
  };

  const restore = async (id) => {
    if (!window.confirm(T.restore_confirm)) return;
    setBusy(true); setErr("");
    try {
      await api.post(`/admin/system/archives/${id}/restore`);
      setToast(T.restore_done);
      setTimeout(() => setToast(""), 5000);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "error");
    } finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm(T.del_confirm)) return;
    setBusy(true); setErr("");
    try {
      await api.delete(`/admin/system/archives/${id}`);
      await load();
    } catch (e) {
      setErr(e?.response?.data?.detail || "error");
    } finally { setBusy(false); }
  };

  const downloadHref = (id) => {
    const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/system/archives/${id}/download`;
    return url;
  };

  return (
    <section
      data-testid="system-reset-card"
      dir={dir}
      style={{
        background: theme.surfaceCard,
        border: `1.5px solid rgba(220,38,38,0.45)`,
        borderRadius: 16, padding: 20, marginBottom: 20,
      }}
    >
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 16, fontWeight: 900, color: "#dc2626" }}>
          {T.title}
        </div>
      </div>
      <p style={{ fontSize: 12.5, lineHeight: 1.8, color: theme.textSoft, marginBottom: 14 }}>
        {T.desc}
      </p>

      {toast && (
        <div style={{
          background: "rgba(22,163,74,0.12)", border: "1px solid rgba(22,163,74,0.45)",
          color: "#16a34a", padding: 10, borderRadius: 10, fontSize: 12, marginBottom: 10, fontWeight: 700,
        }} data-testid="reset-toast">{toast}</div>
      )}
      {err && (
        <div style={{
          background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
          color: theme.errorText, padding: 10, borderRadius: 10, fontSize: 12, marginBottom: 10,
        }} data-testid="reset-error">{err}</div>
      )}

      {step === 0 && (
        <>
          <label style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6, display: "block" }}>{T.label_input}</label>
          <input
            type="text"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder={T.label_ph}
            maxLength={120}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 10,
              border: `1px solid ${theme.inputBorder}`, background: theme.inputBg,
              color: theme.text, fontSize: 13, fontFamily: "inherit", boxSizing: "border-box",
              marginBottom: 12,
            }}
            data-testid="reset-label-input"
          />
          <button
            onClick={startReset}
            disabled={busy}
            data-testid="open-reset-confirm"
            style={{
              width: "100%", padding: "12px 18px",
              background: "linear-gradient(135deg,#dc2626,#b91c1c)",
              color: "#fff", border: "none", borderRadius: 10,
              fontSize: 14, fontWeight: 900, cursor: "pointer",
              letterSpacing: 0.4, fontFamily: "inherit",
            }}
          >
            {T.btn_archive_reset}
          </button>
        </>
      )}

      {step === 1 && (
        <div style={{
          padding: 14, borderRadius: 12,
          background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.45)",
        }} data-testid="reset-confirm-box">
          <div style={{ fontWeight: 900, fontSize: 14, color: "#dc2626", marginBottom: 8 }}>{T.confirm_title}</div>
          <p style={{ fontSize: 12.5, lineHeight: 1.8, color: theme.textSoft, marginBottom: 12 }}>{T.confirm_body}</p>
          <input
            type="text"
            value={confirmPhrase}
            onChange={(e) => setConfirmPhrase(e.target.value)}
            placeholder={T.confirm_input_ph}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 10,
              border: `1px solid ${theme.inputBorder}`, background: theme.inputBg,
              color: theme.text, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
              marginBottom: 12, textAlign: "center", fontWeight: 800, letterSpacing: 2,
            }}
            data-testid="reset-confirm-input"
          />
          <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
            <button onClick={cancel} disabled={busy} style={{
              padding: "8px 16px", background: "transparent", color: theme.text,
              border: `1px solid ${theme.border}`, borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
            }} data-testid="reset-cancel">{T.cancel}</button>
            <button onClick={doReset} disabled={busy} style={{
              padding: "8px 16px", background: "linear-gradient(135deg,#dc2626,#b91c1c)",
              color: "#fff", border: "none", borderRadius: 8, fontWeight: 900, cursor: "pointer", fontSize: 13,
            }} data-testid="reset-do-it">{busy ? "…" : T.do_it}</button>
          </div>
        </div>
      )}

      {/* Archive list */}
      <div style={{ marginTop: 18 }}>
        <div style={{ fontSize: 13, fontWeight: 800, color: theme.gold, marginBottom: 8 }}>{T.archives_title}</div>
        {archives.length === 0 ? (
          <div style={{ fontSize: 12, color: theme.textMuted, padding: 12, textAlign: "center" }}>{T.no_archives}</div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 8 }} data-testid="archive-list">
            {archives.map((a) => (
              <div key={a.id} style={{
                background: theme.surface, border: `1px solid ${theme.borderSoft}`,
                borderRadius: 10, padding: "10px 12px",
                display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: 8,
              }} data-testid={`archive-row-${a.id}`}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ fontWeight: 800, fontSize: 13 }}>{a.label}</div>
                  <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 2 }}>
                    {fmtDateTime(a.created_at)} • {a.created_by_name || "—"} • {fmtNum(a.total_documents)} {T.col_total}
                  </div>
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <a
                    href={downloadHref(a.id)}
                    style={{
                      padding: "6px 10px", background: theme.goldGradient,
                      color: theme.name === "dark" ? "#0a0a0a" : "#fff",
                      borderRadius: 8, fontWeight: 800, fontSize: 11, textDecoration: "none",
                      display: "inline-flex", alignItems: "center", gap: 4,
                    }}
                    data-testid={`archive-download-${a.id}`}
                  >{T.download}</a>
                  <button onClick={() => restore(a.id)} disabled={busy} style={{
                    padding: "6px 10px", background: "transparent",
                    border: `1px solid ${theme.borderAccent}`, color: theme.text,
                    borderRadius: 8, fontWeight: 800, fontSize: 11, cursor: "pointer",
                  }} data-testid={`archive-restore-${a.id}`}>{T.restore}</button>
                  <button onClick={() => del(a.id)} disabled={busy} style={{
                    padding: "6px 10px", background: "transparent",
                    border: "1px solid rgba(220,38,38,0.5)", color: "#dc2626",
                    borderRadius: 8, fontWeight: 800, fontSize: 11, cursor: "pointer",
                  }} data-testid={`archive-delete-${a.id}`}>{T.del}</button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}

function Stat({ label, value, color, theme, testId }) {
  return (
    <div data-testid={testId} style={{
      background: theme.surface, border: `1px solid ${theme.borderSoft}`,
      borderRadius: 12, padding: 14,
    }}>
      <div style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6 }}>{label}</div>
      <div style={{ fontSize: 20, fontWeight: 900, color: color || theme.gold }}>{value || "0"}</div>
    </div>
  );
}

const makeStyles = (theme) => ({
  page: {
    minHeight: "100vh", background: theme.bg, color: theme.text,
    fontFamily: "'Tajawal','Segoe UI',sans-serif",
    maxWidth: 1100, margin: "0 auto", padding: "20px 16px 60px",
  },
  headerRow: { display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, marginBottom: 16, flexWrap: "wrap" },
  h1: { margin: 0, fontSize: 22, fontWeight: 900, color: theme.gold },
  sub: { fontSize: 12, color: theme.textMuted, marginTop: 4 },
  heroCard: {
    background: theme.surfaceCard,
    border: `1.5px solid ${theme.borderAccent}`,
    borderRadius: 18, padding: 22, marginBottom: 20,
    boxShadow: theme.name === "dark" ? "0 0 0 1px rgba(212,175,55,0.08)" : "0 8px 28px rgba(184,144,42,0.10)",
  },
  heroHead: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8, flexWrap: "wrap", gap: 10 },
  heroTitle: { fontSize: 18, fontWeight: 900, color: theme.gold },
  heroDesc: { fontSize: 13, lineHeight: 1.8, color: theme.textSoft, marginBottom: 14 },
  heroMeta: { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 14 },
  versionBadge: {
    background: theme.goldGradient, color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    padding: "6px 14px", borderRadius: 999, fontSize: 12, fontWeight: 900, letterSpacing: 0.5,
  },
  label: { fontSize: 11, color: theme.textMuted, marginBottom: 6, display: "block" },
  input: {
    width: "100%", padding: "11px 13px", borderRadius: 10,
    border: `1px solid ${theme.inputBorder}`, background: theme.inputBg,
    color: theme.text, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
    marginBottom: 14,
  },
  forceBtn: {
    width: "100%", padding: "14px 24px",
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 12, fontSize: 15, fontWeight: 900,
    cursor: "pointer", letterSpacing: 0.4,
    display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8,
  },
  backBtn: {
    background: "transparent", border: `1px solid ${theme.border}`,
    color: theme.text, padding: "8px 14px", borderRadius: 8, cursor: "pointer", fontSize: 13,
    display: "inline-flex", alignItems: "center", gap: 6,
  },
  grid3: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 12, marginBottom: 16 },
  gridMetrics: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 10 },
  err: {
    background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
    color: theme.errorText, padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 12,
  },
  toast: {
    background: "rgba(22,163,74,0.12)",
    border: "1px solid rgba(22,163,74,0.4)",
    color: "#16a34a", padding: 12, borderRadius: 10, fontSize: 13, marginBottom: 12, fontWeight: 700,
  },
  modalBg: {
    position: "fixed", inset: 0, background: "rgba(0,0,0,0.55)", zIndex: 9999,
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16,
  },
  modal: {
    background: theme.surface, border: `1px solid ${theme.borderAccent}`,
    borderRadius: 14, padding: 22, maxWidth: 420, width: "100%",
    color: theme.text, fontFamily: "inherit",
  },
  modalTitle: { fontWeight: 900, fontSize: 16, color: theme.gold, marginBottom: 10 },
  modalBody: { fontSize: 13, lineHeight: 1.8, color: theme.textSoft, marginBottom: 10 },
  msgPreview: {
    background: theme.surfaceAlt, border: `1px dashed ${theme.borderSoft}`,
    padding: "8px 12px", borderRadius: 8, fontSize: 12, fontStyle: "italic", marginBottom: 14,
  },
  modalActions: { display: "flex", justifyContent: "flex-end", gap: 10 },
  btnGhost: {
    padding: "10px 18px", background: "transparent", color: theme.text,
    border: `1px solid ${theme.border}`, borderRadius: 8, fontWeight: 700, cursor: "pointer", fontSize: 13,
  },
  btnDanger: {
    padding: "10px 18px",
    background: theme.goldGradient,
    color: theme.name === "dark" ? "#0a0a0a" : "#fff",
    border: "none", borderRadius: 8, fontWeight: 900, cursor: "pointer", fontSize: 13,
  },
});


/**
 * FinanceResetCard
 * ────────────────
 * STAS-only quick action that zeros the accountant dashboard:
 *   • Wipes payments, ledger, earnings, cash drawer, expenses, salaries
 *   • Resets pricing_items + fees fields on every existing application
 *   • DOES NOT touch the applications themselves, clients, roles, or
 *     branding — so the office keeps operating from a clean slate.
 */
function FinanceResetCard({ theme, lang, dir }) {
  const [step, setStep] = useState(0);
  const [busy, setBusy] = useState(false);
  const [confirm, setConfirm] = useState("");
  const [result, setResult] = useState(null);
  const [err, setErr] = useState("");
  const REQUIRED = "صفر";

  const run = async () => {
    if (confirm.trim() !== REQUIRED) {
      setErr(`اكتب "${REQUIRED}" بالضبط للتأكيد`);
      return;
    }
    setBusy(true); setErr("");
    try {
      const { data } = await api.post("/admin/system/finance-reset", {});
      setResult(data);
      setStep(2);
    } catch (e) {
      setErr(e?.response?.data?.detail || e?.message || "حدث خطأ");
    } finally { setBusy(false); }
  };

  return (
    <section
      data-testid="finance-reset-card"
      dir={dir}
      style={{
        background: theme.surfaceCard,
        border: `1.5px solid rgba(245,158,11,0.55)`,
        borderRadius: 16, padding: 20, marginBottom: 20,
      }}
    >
      <div style={{ fontSize: 16, fontWeight: 900, color: "#f59e0b", marginBottom: 6 }}>
        تصفير لوحة المحاسب (المالية فقط)
      </div>
      <p style={{ fontSize: 12.5, lineHeight: 1.8, color: theme.textSoft, marginBottom: 14 }}>
        يحذف جميع: <strong>المدفوعات</strong>، <strong>القيود المحاسبية</strong>،
        <strong>أرباح الموظفين الشهرية</strong>، <strong>الصندوق النقدي</strong>،
        <strong>المصاريف</strong>، <strong>الرواتب</strong>، <strong>أقفال الفترات</strong>،
        و<strong>مراجعات الفواتير</strong>.
        ويصفّر بنود الأسعار + المبلغ + رقم الفاتورة + حالة الدفع على كل المعاملات الموجودة.
        <br /><br />
        <strong style={{ color: "#16a34a" }}>يبقى محفوظاً:</strong> المعاملات نفسها،
        ملفات العملاء، الأدوار، فهرس الأسعار، الإعدادات.
      </p>

      {err && (
        <div style={{
          background: theme.errorBg, border: `1px solid ${theme.errorBorder}`,
          color: theme.errorText, padding: 10, borderRadius: 10, fontSize: 12, marginBottom: 10,
        }} data-testid="finance-reset-error">{err}</div>
      )}

      {step === 2 && result && (
        <div style={{
          background: "rgba(22,163,74,0.10)", border: "1px solid #16a34a",
          color: "#16a34a", padding: 12, borderRadius: 10, fontSize: 12, marginBottom: 10, lineHeight: 1.8,
        }} data-testid="finance-reset-done">
          <div style={{ fontWeight: 900, marginBottom: 6 }}>تم التصفير بنجاح</div>
          {Object.entries(result.counts || {}).map(([k, v]) => (
            <div key={k} style={{ fontSize: 11 }} dir="ltr">
              <code>{k}</code>: {v}
            </div>
          ))}
        </div>
      )}

      {step === 0 && (
        <button
          onClick={() => setStep(1)}
          data-testid="finance-reset-open"
          style={{
            width: "100%", padding: "12px 18px",
            background: "linear-gradient(135deg,#f59e0b,#b45309)",
            color: "#fff", border: "none", borderRadius: 10,
            fontSize: 14, fontWeight: 900, cursor: "pointer",
            letterSpacing: 0.4, fontFamily: "inherit",
          }}
        >
          بدء تصفير اللوحة المالية
        </button>
      )}

      {step === 1 && (
        <>
          <label style={{ fontSize: 11, color: theme.textMuted, marginBottom: 6, display: "block" }}>
            اكتب كلمة <strong>"{REQUIRED}"</strong> للتأكيد:
          </label>
          <input
            type="text"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            placeholder={REQUIRED}
            style={{
              width: "100%", padding: "10px 12px", borderRadius: 10,
              border: `1.5px solid #f59e0b`, background: theme.inputBg,
              color: theme.text, fontSize: 14, fontFamily: "inherit", boxSizing: "border-box",
              marginBottom: 12, textAlign: "center", fontWeight: 900,
            }}
            data-testid="finance-reset-confirm-input"
          />
          <div style={{ display: "flex", gap: 8 }}>
            <button onClick={() => { setStep(0); setConfirm(""); setErr(""); }}
              style={{
                flex: 1, padding: "12px", borderRadius: 10,
                background: "transparent", color: theme.text,
                border: `1px solid ${theme.border}`, fontWeight: 800, fontSize: 13, cursor: "pointer",
              }}>
              إلغاء
            </button>
            <button onClick={run} disabled={busy || confirm.trim() !== REQUIRED}
              data-testid="finance-reset-execute"
              style={{
                flex: 2, padding: "12px",
                background: confirm.trim() === REQUIRED ? "#dc2626" : "rgba(220,38,38,0.30)",
                color: "#fff", border: "none", borderRadius: 10,
                fontSize: 13, fontWeight: 900, cursor: confirm.trim() === REQUIRED ? "pointer" : "not-allowed",
              }}>
              {busy ? "جاري التصفير..." : "نفّذ التصفير"}
            </button>
          </div>
        </>
      )}
    </section>
  );
}
